import Accordion, { AccordionItem } from '@components/ui/Accordion/Accordion'
import LineClamp from '@components/ui/LineClampV2'
import useMediaQuery from '@lib/hooks/useMediaQuery'
import {
  M64AccordionsFragment,
  MediaWithCropsFragment,
} from '@lib/umbraco/generated/types.generated'
import {
  selectChannel,
  selectCountry,
  selectEcommerceEnabled,
  selectProductScrollTimeout,
} from '@redux/globalSlice'
import gsap from 'gsap'
import { useQueryState } from 'next-usequerystate'

import { UmbracoLink } from '@customTypes/umbracoLink'
import { getOrigin } from '@lib/get-origin'
import {
  ProductDetailFragment,
  ProductDetailVariantFragment,
} from '@lib/graphql/graphql'
import { generateMediaUrlV2 } from '@lib/utils'
import { mapEdgesToItems } from '@utils/map'
import {
  getProductCategory,
  getTrackingImageUrlV2,
  getTrackingProductUrl,
  pushProductViewTrackingEvent,
} from '@utils/tracking-utils'
import Head from 'next/head'
import { useRouter } from 'next/router'
import { getTaxonomyId } from 'pages/[language]/[country]/google.shopping.xml'
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import mergeRefs from 'react-merge-refs'
import { useSelector } from 'react-redux'
import invariant from 'tiny-invariant'
import BackInStockModal from './BackInStockModal/BackInStock'
import s from './Product.module.scss'
import ProductImages from './ProductImages/ProductImages'
import ProductSidebar from './ProductSidebar/ProductSidebar'
import Specificaitons from './Specifications/Specificaitons'

import ARButtonWrapper from '@components/common/SublimeMedia/ARButton/ARButtonWrapper'
import ArIcon from '@components/ui/Icons/ArIcon'
import { getTrackingProductId } from '@features/products/utils'
import { centAmountToAmountV2 } from '@lib/format-price'
import { getStockAvailability } from '@lib/get-stock-availability'
import { STOCK_STATUS } from 'config'
import {
  generateAttributeGroupsV2,
  getProductName,
  getPurchaseDisabled,
  getSpecifications,
  getVariantFromSelectedOptions,
} from './helpersv2'
export interface ProductProps {
  extraProperties: {
    media: MediaWithCropsFragment
    scrollTimeout: number
    fontStyle: string
    designerLink: UmbracoLink
    retailerTags: string[] | null
    configuratorLink: UmbracoLink | null
    configuratorLinkAttribute: string | null
  }
  productV2: ProductDetailFragment
  accordions: M64AccordionsFragment['accordions']
}

const schemaStockState = (
  status: STOCK_STATUS,
  purchaseDisabled: boolean,
): string | undefined => {
  if (purchaseDisabled) return undefined

  switch (status) {
    case STOCK_STATUS.IN_STOCK:
    case STOCK_STATUS.REMOTE_STOCK:
      return 'https://schema.org/InStock'
    case STOCK_STATUS.MADE_TO_ORDER:
      return 'https://schema.org/BackOrder'
    default:
      return undefined //return 'https://schema.org/OutOfStock'
  }
}
const Product: FC<ProductProps> = ({
  productV2,
  extraProperties,
  accordions,
}) => {
  const imgContainerRef = useRef<HTMLDivElement>(null)
  const isBreakingPoint = useMediaQuery('(max-width: 1024px)')
  const router = useRouter()
  const formRef = useRef<HTMLFormElement>(null)
  const { itemNumber, language } = router.query
  const country = useSelector(selectCountry)
  const channel = useSelector(selectChannel)
  const ecommerceEnabled = useSelector(selectEcommerceEnabled)
  const [backInStockOpen, setBackInStockOpen] = useState(false)
  const [itemNumberQuery, setItemNumberQuery] = useQueryState('itemNumber')
  const variants = mapEdgesToItems(productV2.variants)

  const [selectedVariantV2, setSelectedVariantV2] =
    useState<ProductDetailVariantFragment>(
      variants.find((x) => x.sku === (itemNumber as string)) ??
        variants.find((x) => x.sku === productV2.heroVariant.sku) ??
        variants.find(Boolean),
    )

  const productRef = useRef<HTMLFormElement>(null)
  const [hasScrolled, setHasScrolled] = useState(false)

  const [inViewRef, inView] = useInView({
    threshold: 0,
    initialInView: true,
  })
  const productName = useMemo(() => getProductName(productV2.name), [productV2])
  const attributeGroups = useMemo(
    () => generateAttributeGroupsV2(productV2, selectedVariantV2.sku ?? ''),
    [selectedVariantV2],
  )

  const specifications = useMemo(
    () => getSpecifications(selectedVariantV2.metadata),
    [selectedVariantV2],
  )
  const purchaseDisabled = useMemo(
    () => !ecommerceEnabled || getPurchaseDisabled(selectedVariantV2, country),
    [selectedVariantV2, country],
  )

  const imagePadding = !selectedVariantV2?.metadata?.find(
    (x) => x.key == 'DISABLE_IMAGE_PADDING',
  )

  const applyForceDisplayLogic = country.code === 'US'
  // const forceDisplayFullPrice = useMemo(
  //   () =>
  //     applyForceDisplayLogic &&
  //     (selectedVariant.stockAvailability.state == 'OUT_OF_STOCK' ||
  //       (selectedVariant.stockAvailability.state == 'IN_STOCK' &&
  //         selectedVariant.stockAvailability.quantity == 0)),
  //   [selectedVariant, applyForceDisplayLogic],
  // )

  const scrollTimeout = useSelector(selectProductScrollTimeout)

  const accordionItems = useMemo(() => {
    const items: AccordionItem[] = [
      {
        title: 'SPECIFICATIONS',
        children: <Specificaitons specifications={specifications} />,
      },
    ]

    accordions &&
      accordions.map((accordion) => {
        invariant(accordion.__typename === 'Accordion', 'Invalid type')

        if (accordion.accordionItems.length === 1) {
          invariant(
            accordion.accordionItems[0].__typename === 'M64AccordionItem',
            'Invalid type',
          )
          items.push({
            title: accordion.accordionItems[0].itemTitle.toUpperCase(),
            children: <></>,
            type: 'link',
            href: accordion.accordionItems[0].itemLink.url,
            external: accordion.accordionItems[0].itemLink.target === '_blank',
          })
        } else {
          items.push({
            title: accordion.headline,
            children: (
              <Specificaitons
                type={'special'}
                specifications={
                  accordion.accordionItems
                    .filter((items) => items.__typename === 'M64AccordionItem')
                    .map((items) => items) as any[]
                }
              />
            ),
          })
        }
      })

    selectedVariantV2.metadata.some(
      (x) =>
        x.key === 'ODOO_SPEC_UpholsteryType' &&
        x.value !== '' &&
        x.value !== undefined &&
        x.value !== null &&
        x.value !== 'Un-Upholstered',
    ) &&
      items.push({
        title: 'EXPLORE UPHOLSTERY',
        children: <></>,
        type: 'link',
        href: 'https://gubi.com/upholstery',
        external: true,
      })

    return items
  }, [specifications, productV2])

  // const arFile = useMemo(
  //   () => getArFile(selectedVariant, product),
  //   [selectedVariant, product],
  // )

  const handleFormChanges = useCallback(
    (e: React.ChangeEvent<HTMLFormElement>) => {
      //get checked swatches from formRef.current
      const checkedOptions = Array.from(
        formRef.current?.querySelectorAll('input:checked') ?? [],
      ) as HTMLInputElement[]

      const variant = getVariantFromSelectedOptions(productV2, checkedOptions)

      if (variant) {
        const tl = gsap.timeline({})
        tl.to(imgContainerRef.current, {
          autoAlpha: 0,
          duration: 0.2,
        })
          .add(() => {
            setSelectedVariantV2(variant)
          })
          .to(
            imgContainerRef.current,
            {
              autoAlpha: 1,
              duration: 0.3,
            },
            '+=0.3',
          )
        if (!isBreakingPoint) {
          window.scrollTo({
            top: formRef.current.offsetTop,
            behavior: 'smooth',
          })
        }
      }
    },
    [productV2],
  )

  //xxx
  useEffect(() => {
    if (!itemNumberQuery && selectedVariantV2.sku == productV2.heroVariant.sku)
      return
    setItemNumberQuery(selectedVariantV2.sku.toString(), {
      scroll: false,
      shallow: true,
    })
  }, [selectedVariantV2])

  useEffect(() => {
    const timeout = setTimeout(() => {
      // prevent pushing multiple events when changing fast / on load / re-render
      pushProductViewTrackingEvent({
        trackingList: 'Product Detail Page',
        currencyCode: selectedVariantV2.price?.value.currencyCode,
        products: [
          {
            id: getTrackingProductId(productV2.id, productV2.metadata),
            name: productV2?.name,
            variant: selectedVariantV2?.name,
            category: getProductCategory(productV2.tags.map((tag) => tag)),
            image_url: getTrackingImageUrlV2(
              mapEdgesToItems(selectedVariantV2?.media).map((x) => x.src),
            ),
            url: getTrackingProductUrl(productV2.slug, selectedVariantV2?.sku),
            brand: productV2.vendor,
            // price: trackingEventVariantPrice(
            //   selectedVariant?.price,
            //   selectedVariant?.discountedPrice?.value,
            //   // forceDisplayFullPrice,
            // ),
            dimension1: selectedVariantV2?.sku,
            dimension2: getStockAvailability(
              channel,
              selectedVariantV2.metadata,
            ),
          },
        ],
      })
    }, 250)

    return () => clearTimeout(timeout)
  }, [selectedVariantV2])

  // if productScrollBanner is in view, scroll to formRef after 3 seconds if not already in view
  useEffect(() => {
    if (inView || !scrollTimeout) {
      return
    }
    if (window.scrollY == 0 && !hasScrolled) {
      const timeout = setTimeout(() => {
        if (!inView) {
          window.scrollTo({
            top: formRef.current.offsetTop + 32,
            behavior: 'smooth',
          })
        }
      }, scrollTimeout * 1000)

      setHasScrolled(true)

      return () => clearTimeout(timeout)
    }
  }, [inView, scrollTimeout])

  const variesBy = useMemo(() => {
    const swatchAttributeNames = productV2.attributeAssignments
      ?.filter(
        (attr) =>
          attr.name.toLowerCase().includes('color') ||
          attr.name.toLowerCase().includes('colour') ||
          attr.name.toLowerCase().includes('size'),
      )
      .map((attr) => attr.name.toLowerCase())

    const result = []

    if (
      swatchAttributeNames.some(
        (name) => name.includes('color') || name.includes('colour'),
      )
    ) {
      result.push('https://schema.org/color')
    }

    if (swatchAttributeNames.some((name) => name.includes('size'))) {
      result.push('https://schema.org/size')
    }

    return result
  }, [productV2])

  // get arFile from productV2 metadata. However, if selectedVariantV2 has a metadata key 'S3_AR_File', use that instead
  const arFile = useMemo(() => {
    const arFileMetadata = selectedVariantV2.metadata.find(
      (x) => x.key === 'S3_AR_FILE',
    )
    if (arFileMetadata) {
      return {
        s3FileName: arFileMetadata.value,
      }
    } else {
      const arFileMetadata = productV2.metadata.find(
        (x) => x.key === 'S3_AR_FILE',
      )
      if (arFileMetadata) {
        return {
          s3FileName: arFileMetadata.value,
        }
      }
    }
    return null
  }, [selectedVariantV2])

  return (
    <>
      <form
        onChange={handleFormChanges}
        ref={mergeRefs([formRef, inViewRef, productRef])}
        className={s['product-form']}
      >
        <div className={s['product-view']}>
          <div className={s['product-main']}>
            <div className={s['sticky-content']}>
              {/* <button type="button" onClick={(e) => {
                                e.preventDefault();
                                // history.back();
                                router.back()
                            }} className={s["back-btn"]}>
                                <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.89453 10.991L0.999028 6.09546L5.89453 1.19996" stroke="black" />
                                    <line x1="1.30469" y1="6.09998" x2="13.5434" y2="6.09998" stroke="black" />
                                </svg>
                            </button> */}

              {arFile && (
                <ARButtonWrapper iosSrc={arFile.s3FileName}>
                  <span className={s['ar-icon']}>
                    <span className={s['ar-icon-text']}>Explore with</span>
                    <ArIcon />
                  </span>
                </ARButtonWrapper>
              )}
            </div>
            <ProductImages
              className={s['product-images']}
              ref={imgContainerRef}
              images={mapEdgesToItems(selectedVariantV2?.media) ?? []}
              padding={imagePadding}
              selectedVariant={selectedVariantV2}
            />
          </div>
          <ProductSidebar
            productName={productName.name}
            productSecondaryName={productName.secondaryName}
            attributeGroups={attributeGroups}
            productV2={productV2}
            selectedVariantV2={selectedVariantV2}
            purchaseDisabled={purchaseDisabled}
            metadata={selectedVariantV2.metadata}
            productModuleInView={inView}
            designerLink={extraProperties?.designerLink}
            retailerTags={extraProperties?.retailerTags}
            configuratorLink={extraProperties?.configuratorLink}
            configuratorLinkAttribute={
              extraProperties?.configuratorLinkAttribute
            }
            setBackInStockModalOpen={setBackInStockOpen}
            productRef={productRef}
          />
        </div>
        <div className={s['product-additional']}>
          <div className={s['product-accordion']}>
            <span className={s['variant-item-number']}>
              ITEM NO: {selectedVariantV2.sku}
            </span>
            <Accordion items={accordionItems} />
          </div>
          <div className={s['product-additonal-description']}>
            <p className={s['product-description']}>
              <LineClamp
                lines={4}
                fontSize={19}
                text={productV2.description}
              ></LineClamp>
            </p>
          </div>
        </div>
      </form>
      <BackInStockModal
        itemNumber={selectedVariantV2.sku}
        isOpen={backInStockOpen}
        setOpen={setBackInStockOpen}
        data={productName.name}
      />

      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org/',
              '@type': 'ProductGroup',
              name: productV2?.name,
              // image: [
              //   selectedVariant.media.map((x) =>
              //     generateMediaUrl(x).replace('{size}', '1920'),
              //   ),
              // ],
              description: productV2.description,
              url: `${getOrigin()}/${language}/${country.code?.toLowerCase() + '/products' + productV2.slug}`,
              brand: {
                '@type': 'Brand',
                name: 'GUBI',
              },
              productGroupID: productV2.id,
              ...(variesBy.length > 0 && { variesBy }),
              // google_product_category: product, // TODO: Proper category from product tags
              hasVariant: variants.map((variant) => {
                const color = variant.selectedAttributes.find(
                  (attr) =>
                    attr.name.toLowerCase().includes('color') ||
                    attr.name.toLowerCase().includes('colour'),
                )?.value

                const size = variant.selectedAttributes.find((attr) =>
                  attr.name.toLowerCase().includes('size'),
                )?.value

                return {
                  '@type': 'Product',
                  sku: variant.sku,
                  productID: variant.sku,
                  gtin: variant.barcode,
                  image: [
                    mapEdgesToItems(variant.media).map((x) =>
                      generateMediaUrlV2(x.src).replace('{size}', '1920'),
                    ),
                  ],
                  name: variant.name,
                  ...(color && { color }),
                  ...(size && { size }),
                  offers: {
                    '@type': 'Offer',
                    url: `${getOrigin()}/${language}/${country.code?.toLowerCase()}/products/${productV2.slug}?itemNumber${variant.sku}`,
                    price: centAmountToAmountV2(
                      variant.price?.value,
                    ).toString(),
                    priceCurrency: selectedVariantV2.price?.value.currencyCode,
                    availability: schemaStockState(
                      getStockAvailability(channel, variant.metadata),
                      !variant.availability.availableForPurchase,
                    ),
                    itemCondition: 'https://schema.org/NewCondition',
                  },
                }
              }),
            }),
          }}
        />
        {variants.map((variant) => {
          return (
            <meta
              className="elastic"
              key={variant.sku}
              name="item_number"
              content={variant.sku}
            />
          )
        })}
        <meta className="elastic" name="product_id" content={productV2.id} />
        <meta property="product:brand" content="GUBI" />
        <meta property="product:availability" content="in stock" />
        <meta property="product:condition" content="new" />
        <meta
          property="product:price:amount"
          content={centAmountToAmountV2(
            selectedVariantV2.price?.value,
          ).toString()}
        />
        <meta
          property="product:price:currency"
          content={selectedVariantV2.price?.value.currencyCode}
        />
        <meta
          property="product:locale"
          content={`${language}_${(country.code as string)?.toUpperCase()}`}
        />
        <meta
          property="product:retailer_item_id"
          content={selectedVariantV2.sku}
        />
        <meta property="product:item_group_id" content={productV2.id} />
        <meta
          property="product:category"
          content={getTaxonomyId(productV2.tags.map((tag) => tag)) ?? ''}
        />
      </Head>
    </>
  )
}

export default Product
